import {
  Badge,
  Group,
  Button as MantineButton,
  Popover,
  Stack,
  Table,
  TableTbody,
  TableTd,
  TableTr,
  Text,
  Title,
} from "@mantine/core"
import { useDisclosure } from "@mantine/hooks"
import { IconPencil, IconUserPlus } from "@tabler/icons-react"
import { Button } from "@tfg/ui"
import { useTranslation } from "react-i18next"
import { useOutletContext } from "react-router-dom"

import { AdminResponses } from "@tfg/types/endpoints/admin"

import { AnonymizeUserButton } from "~/pages/Admin/Organizations/AnonymizeUserButton/AnonymizeUserButton"
import { ChangeBusinessType } from "~/pages/Admin/Organizations/ChangeBusinessType"
import { InviteUsersDrawer } from "~/pages/Admin/Organizations/InviteUsersDrawer"
import { ToggleOrganizationUsersStatusButton } from "~/pages/Admin/Organizations/ToggleOrganizationUsersStatusButton/ToggleOrganizationUsersStatusButton"
import { ToggleUserStatusButton } from "~/pages/Admin/Organizations/ToggleUserStatusButton/ToggleUserStatusButton"
import { GenerateMagicLinkButton } from "~/pages/Admin/components/GenerateMagicLink"
import { MuteButton } from "~/pages/Brand/Suppliers/List/components/MuteButton"
import { useAuth } from "~/store/auth-context"

import { ChangePlanModal } from "./ChangePlanModal"
import { useCreateCountryContact, useLoginAsUserMutation } from "./hooks"

export const OrganizationDetails = () => {
  const [addUsersOpen, addUsersActions] = useDisclosure(false)

  const organization = useOutletContext<AdminResponses.OrganizationDetails>()

  return (
    <Stack>
      <Stack gap="xs">
        <Title>{organization.name}</Title>
        <Text size="xs" c="dimmed">
          Created on{" "}
          {new Intl.DateTimeFormat().format(new Date(organization.createdAt))}
        </Text>
      </Stack>
      <Stack gap="xs">
        <Title order={2}>Features settings</Title>
        <AGECPlan organization={organization} />
        <OutputCustomizationPlan organization={organization} />
        <LCAPlan organization={organization} />
        <CustomFieldsPlan organization={organization} />
        <CertificatesPlan organization={organization} />
        <ZalandoPlan organization={organization} />
        <RduePlan organization={organization} />
      </Stack>
      <Stack>
        <Title order={2}>Other settings</Title>
        <ChangeBusinessType organization={organization} />
      </Stack>
      <Stack gap="xs">
        <Group justify="space-between">
          <Title order={2}>Users</Title>
          <Group gap={4}>
            <ToggleOrganizationUsersStatusButton
              organizationId={organization.id}
            />
            <Button
              onClick={addUsersActions.open}
              icon={<IconUserPlus size={16} />}
            >
              Create Users
            </Button>
          </Group>
        </Group>
        {organization.users.length === 0 ? (
          <Text>No users</Text>
        ) : (
          <Table verticalSpacing="sm" highlightOnHover>
            <Table.Tbody>
              {organization.users.map((user) => (
                <TableTr key={user.id}>
                  <TableTd width="200">
                    <Group>
                      <Stack gap={4}>
                        <Text fz="sm" style={{ fontWeight: "bold" }}>
                          {user.name}
                        </Text>
                        <Text fz="sm">{user.email}</Text>
                      </Stack>
                    </Group>
                  </TableTd>
                  <TableTd style={{ textAlign: "right" }}>
                    <Group gap={4} justify="flex-end">
                      <LoginAs
                        user={user}
                        withoutAdmin={true}
                        onLogin={() => window.location.replace("/")}
                      />
                      <ToggleUserStatusButton
                        companyId={organization.companyId}
                        organizationId={organization.id}
                        user={user}
                      />
                      <AnonymizeUserButton
                        organizationId={organization.id}
                        user={user}
                      />
                    </Group>
                  </TableTd>
                </TableTr>
              ))}
            </Table.Tbody>
          </Table>
        )}
      </Stack>
      <Stack gap="xs">
        <Group justify="space-between">
          <Title order={3}>Suppliers</Title>
          <CreateCountryContactButton
            userEmails={organization.suppliers.map((x) => x.user.email)}
          />
        </Group>
        {organization.suppliers.length === 0 ? (
          <Text>No suppliers</Text>
        ) : (
          <Table verticalSpacing="sm" highlightOnHover>
            <Table.Tbody>
              {organization.suppliers.map((supplier) => (
                <TableTr
                  data-testid={supplier.user.email}
                  key={supplier.user.id}
                >
                  <TableTd width="200">
                    <Text style={{ fontWeight: "bold" }}>
                      {supplier.user.name}
                    </Text>
                    <Text fz="sm">{supplier.user.email}</Text>
                  </TableTd>
                  <TableTd>
                    <Stack
                      align="flex-start"
                      gap="var(--tfg-molecule-padding-y)"
                    >
                      <LoginAs
                        user={supplier.user}
                        fullWidth={true}
                        withoutAdmin={true}
                        onLogin={() =>
                          window.open("/supplier/requests", "_blank")
                        }
                      />
                      <CreateCountryContactButton
                        userEmails={[supplier.user.email]}
                      />
                    </Stack>
                  </TableTd>

                  <TableTd>
                    <Stack mah={200} style={{ overflowY: "scroll" }} maw={200}>
                      {supplier.contactForProductionSite.map((c) => (
                        <Group key={c.id}>
                          <Text py="sm" fz="sm">
                            {c.name}
                          </Text>
                        </Group>
                      ))}
                    </Stack>
                  </TableTd>
                  <TableTd>
                    <Table>
                      <TableTbody>
                        <TableTr>
                          <TableTd>Information Requests</TableTd>
                          <TableTd>Certificate Requests</TableTd>
                          <TableTd>Is direct for</TableTd>
                        </TableTr>
                        <TableTr>
                          <TableTd>
                            <b>{supplier.informationRequestOpenCount}</b> /{" "}
                            <b>{supplier.informationRequestCloseCount}</b>
                          </TableTd>
                          <TableTd>
                            <b>{supplier.openCertificateRequests}</b> /{" "}
                            <b>{supplier.closedCertificateRequests}</b>
                          </TableTd>
                          <TableTd>
                            <b>{supplier.isDirectForXProduct}</b>
                          </TableTd>
                        </TableTr>
                        <TableTr>
                          <TableTd>Open / Closed</TableTd>
                          <TableTd>Open / Closed</TableTd>
                          <TableTd>Products</TableTd>
                        </TableTr>
                      </TableTbody>
                    </Table>
                  </TableTd>
                  <TableTd>
                    <Table>
                      <TableTbody>
                        <TableTr>
                          <TableTd>
                            <Stack justify="center" gap={8}>
                              <MuteButton
                                userId={supplier.user.id}
                                hasMutedReminders={
                                  supplier.user.hasMutedReminders
                                }
                              />
                              <GenerateMagicLinkButton
                                userId={supplier.user.id}
                              />
                            </Stack>
                          </TableTd>
                        </TableTr>
                      </TableTbody>
                    </Table>
                  </TableTd>
                </TableTr>
              ))}
            </Table.Tbody>
          </Table>
        )}
      </Stack>
      <InviteUsersDrawer
        opened={addUsersOpen}
        organization={organization}
        onClose={addUsersActions.close}
      />
    </Stack>
  )
}

function CustomFieldsPlan({
  organization,
}: {
  organization: AdminResponses.OrganizationDetails
}) {
  const { t } = useTranslation("admin")
  const [isOpen, { open, close }] = useDisclosure(false)
  return (
    <Group>
      <Text fz="sm">{t("plan.customFieldsPlan.label")}</Text>
      <Badge
        size="lg"
        color={
          organization.settings.customFieldsPlan === "ADVANCED"
            ? "red"
            : "green"
        }
      >
        {organization.settings.customFieldsPlan == "ADVANCED" &&
          t(`plan.customFieldsPlan.advanced`)}
        {organization.settings.customFieldsPlan == "EXPERT" &&
          t(`plan.customFieldsPlan.expert`)}
      </Badge>
      <Button variant="tertiary" icon={<IconPencil size={16} />} onClick={open}>
        Change plan
      </Button>
      <ChangePlanModal
        organization={organization}
        data={[
          {
            label: t("plan.customFieldsPlan.advanced"),
            value: "ADVANCED",
          },
          {
            label: t("plan.customFieldsPlan.expert"),
            value: "EXPERT",
          },
        ]}
        opened={isOpen}
        onClose={close}
        planKey="customFieldsPlan"
      />
    </Group>
  )
}

function LCAPlan({
  organization,
}: {
  organization: AdminResponses.OrganizationDetails
}) {
  const { t } = useTranslation("admin")
  const [changeLCAPlanOpen, changeLCAPlanActions] = useDisclosure(false)
  return (
    <Group>
      <Text fz="sm">{t("plan.lcaCustomizationPlan.label")}</Text>
      <Badge
        size="lg"
        color={
          organization.settings.lcaCustomizationPlan === "ADVANCED"
            ? "red"
            : "green"
        }
      >
        {organization.settings.lcaCustomizationPlan == "ADVANCED" &&
          t(`plan.lcaCustomizationPlan.advanced`)}
        {organization.settings.lcaCustomizationPlan == "EXPERT" &&
          t(`plan.lcaCustomizationPlan.expert`)}
      </Badge>
      <Button
        variant="tertiary"
        icon={<IconPencil size={16} />}
        onClick={changeLCAPlanActions.open}
      >
        Change plan
      </Button>
      <ChangePlanModal
        planKey="lcaCustomizationPlan"
        data={[
          {
            label: t("plan.lcaCustomizationPlan.advanced"),
            value: "ADVANCED",
          },
          {
            label: t("plan.lcaCustomizationPlan.expert"),
            value: "EXPERT",
          },
        ]}
        organization={organization}
        opened={changeLCAPlanOpen}
        onClose={changeLCAPlanActions.close}
      />
    </Group>
  )
}

function OutputCustomizationPlan({
  organization,
}: {
  organization: AdminResponses.OrganizationDetails
}) {
  const { t } = useTranslation("admin")
  const [changeOutputPlanOpen, changeOutputPlanActions] = useDisclosure(false)
  return (
    <Group gap="xs">
      <Text fz="sm">{t("plan.outputCustomizationPlan.label")}</Text>
      <Badge
        size="lg"
        color={
          organization.settings.outputCustomizationPlan === "ESSENTIAL"
            ? "red"
            : organization.settings.outputCustomizationPlan === "ADVANCED"
              ? "yellow"
              : "green"
        }
      >
        {organization.settings.outputCustomizationPlan == "ESSENTIAL" &&
          t(`plan.outputCustomizationPlan.essential`)}
        {organization.settings.outputCustomizationPlan == "ADVANCED" &&
          t(`plan.outputCustomizationPlan.advanced`)}
        {organization.settings.outputCustomizationPlan == "EXPERT" &&
          t(`plan.outputCustomizationPlan.expert`)}
      </Badge>
      <Button
        variant="tertiary"
        icon={<IconPencil size={16} />}
        onClick={changeOutputPlanActions.open}
      >
        Change plan
      </Button>
      <ChangePlanModal
        planKey="outputCustomizationPlan"
        data={[
          {
            label: t("plan.outputCustomizationPlan.essential"),
            value: "ESSENTIAL",
          },
          {
            label: t("plan.outputCustomizationPlan.advanced"),
            value: "ADVANCED",
          },
          {
            label: t("plan.outputCustomizationPlan.expert"),
            value: "EXPERT",
          },
        ]}
        organization={organization}
        opened={changeOutputPlanOpen}
        onClose={changeOutputPlanActions.close}
      />
    </Group>
  )
}

function AGECPlan({
  organization,
}: {
  organization: AdminResponses.OrganizationDetails
}) {
  const [changeAGECPlanOpen, changeAGECPlanActions] = useDisclosure(false)
  const { t } = useTranslation("admin")
  return (
    <Group gap="xs">
      <Text fz="sm">{t("plan.agecProductPagePlan.label")}</Text>
      <Badge
        size="lg"
        color={
          organization.settings.agecProductPagePlan === "STARTER"
            ? "red"
            : organization.settings.agecProductPagePlan === "ESSENTIAL"
              ? "yellow"
              : "green"
        }
      >
        {organization.settings.agecProductPagePlan === "STARTER" &&
          t(`plan.agecProductPagePlan.starter`)}
        {organization.settings.agecProductPagePlan === "ESSENTIAL" &&
          t(`plan.agecProductPagePlan.essential`)}
        {organization.settings.agecProductPagePlan === "ADVANCED" &&
          t(`plan.agecProductPagePlan.advanced`)}
      </Badge>
      <Button
        variant="tertiary"
        size="sm"
        icon={<IconPencil size={16} />}
        onClick={changeAGECPlanActions.open}
      >
        Change plan
      </Button>
      <ChangePlanModal
        planKey="agecProductPagePlan"
        data={[
          { label: t("plan.agecProductPagePlan.starter"), value: "STARTER" },
          {
            label: t("plan.agecProductPagePlan.essential"),
            value: "ESSENTIAL",
          },
          {
            label: t("plan.agecProductPagePlan.advanced"),
            value: "ADVANCED",
          },
        ]}
        organization={organization}
        opened={changeAGECPlanOpen}
        onClose={changeAGECPlanActions.close}
      />
    </Group>
  )
}

function CertificatesPlan({
  organization,
}: {
  organization: AdminResponses.OrganizationDetails
}) {
  const [opened, { open, close }] = useDisclosure(false)
  const { t } = useTranslation("admin")
  return (
    <Group gap="xs">
      <Text fz="sm">{t("plan.certificatesPlan.label")}</Text>
      <Badge
        size="lg"
        color={
          organization.settings.certificatesPlan === "ADVANCED"
            ? "red"
            : "green"
        }
      >
        {organization.settings.certificatesPlan === "ADVANCED" &&
          t(`plan.certificatesPlan.advanced`)}
        {organization.settings.certificatesPlan === "EXPERT" &&
          t(`plan.certificatesPlan.expert`)}
      </Badge>
      <Button variant="tertiary" icon={<IconPencil size={16} />} onClick={open}>
        Change plan
      </Button>
      <ChangePlanModal
        planKey="certificatesPlan"
        data={[
          {
            label: t("plan.certificatesPlan.advanced"),
            value: "ADVANCED",
          },
          {
            label: t("plan.certificatesPlan.expert"),
            value: "EXPERT",
          },
        ]}
        organization={organization}
        opened={opened}
        onClose={close}
      />
    </Group>
  )
}

function ZalandoPlan({
  organization,
}: {
  organization: AdminResponses.OrganizationDetails
}) {
  const [opened, { open, close }] = useDisclosure(false)
  const { t } = useTranslation("admin")
  return (
    <Group gap="var(--tfg-molecule-padding-y)">
      <Text fz="sm">{t("plan.zalandoPlan.label")}</Text>
      <Badge
        size="lg"
        color={
          organization.settings.zalandoPlan === "DISABLED" ? "red" : "green"
        }
      >
        {organization.settings.zalandoPlan === "DISABLED" &&
          t(`plan.zalandoPlan.no`)}
        {organization.settings.zalandoPlan === "ENABLED" &&
          t(`plan.zalandoPlan.yes`)}
      </Badge>
      <Button
        variant="tertiary"
        size="sm"
        icon={<IconPencil size={16} />}
        onClick={open}
      >
        Change plan
      </Button>
      <ChangePlanModal
        planKey="zalandoPlan"
        data={[
          {
            label: t("plan.zalandoPlan.no"),
            value: "DISABLED",
          },
          {
            label: t("plan.zalandoPlan.yes"),
            value: "ENABLED",
          },
        ]}
        organization={organization}
        opened={opened}
        onClose={close}
      />
    </Group>
  )
}

function RduePlan({
  organization,
}: {
  organization: AdminResponses.OrganizationDetails
}) {
  const [opened, { open, close }] = useDisclosure(false)
  const { t } = useTranslation("admin")
  return (
    <Group gap="xs">
      <Text fz="sm">{t("plan.rduePlan.label")}</Text>
      <Badge
        size="lg"
        color={organization.settings.rduePlan === "DISABLED" ? "red" : "green"}
      >
        {organization.settings.rduePlan === "DISABLED" && t(`plan.rduePlan.no`)}
        {organization.settings.rduePlan === "ENABLED" && t(`plan.rduePlan.yes`)}
      </Badge>
      <Button
        variant="tertiary"
        size="sm"
        icon={<IconPencil size={16} />}
        onClick={open}
      >
        Change plan
      </Button>
      <ChangePlanModal
        planKey="rduePlan"
        data={[
          {
            label: t("plan.rduePlan.no"),
            value: "DISABLED",
          },
          {
            label: t("plan.rduePlan.yes"),
            value: "ENABLED",
          },
        ]}
        organization={organization}
        opened={opened}
        onClose={close}
      />
    </Group>
  )
}

type LoginAsProps = {
  user: {
    id: string
    email: string
    name: string
  }
  showEmail?: boolean
  withoutAdmin: boolean
  onLogin: () => void
  fullWidth?: boolean
}

export function LoginAs(props: LoginAsProps) {
  const { mutateAsync: loginAsUser } = useLoginAsUserMutation()
  const { checkAuth } = useAuth()
  const { user, withoutAdmin, onLogin, showEmail, fullWidth } = props

  return (
    <>
      <Button
        size="sm"
        variant="primary"
        fullWidth={fullWidth}
        data-user-email={user.email}
        data-testid="login-as-user"
        toolTip={`you will log as ${user.email} but still see admin buttons and page, ALL the action will be registered as if you were ${user.email} (change operation, submiting...)`}
        toolTipProps={{ multiline: true, w: 300 }}
        onClick={() =>
          loginAsUser({
            userId: user.id,
            forwardRole: true,
          }).then(() => {
            checkAuth()
            onLogin()
          })
        }
      >
        Log in as{showEmail && ` ${user.email}`}
      </Button>
      {withoutAdmin && (
        <>
          {" "}
          <Button
            size="sm"
            fullWidth={fullWidth}
            variant="secondary"
            color="secondary"
            toolTip={`you will log exactly is as if you were ${user.email}, and will need to logout first to go back to admin page`}
            toolTipProps={{ multiline: true, w: 300 }}
            onClick={() =>
              loginAsUser({
                userId: user.id,
                forwardRole: false,
              }).then(() => {
                checkAuth()
                onLogin()
              })
            }
          >
            without admin privilege
          </Button>
        </>
      )}
    </>
  )
}

function CreateCountryContactButton({ userEmails }: { userEmails: string[] }) {
  const {
    mutate: createCountryContact,
    isLoading,
    error,
  } = useCreateCountryContact(userEmails)

  const [opened, { open, close }] = useDisclosure(false)

  return (
    <>
      <Popover
        position="bottom"
        withArrow
        shadow="md"
        opened={opened}
        closeOnEscape={true}
        closeOnClickOutside={true}
        onClose={close}
      >
        <Popover.Target>
          <MantineButton
            w="100%"
            loading={isLoading}
            variant="tertiary"
            onClick={open}
            justify="center"
          >
            {userEmails.length > 1 ? (
              <>Create country contact for {userEmails.length} suppliers</>
            ) : (
              <>Create country contact</>
            )}
          </MantineButton>
        </Popover.Target>
        <Popover.Dropdown>
          <Stack>
            <Text size="xs">Are you sure ? it can be hard to rollback</Text>
            <Button
              variant="primary"
              color="red"
              loading={isLoading}
              onClick={() => {
                close()
                createCountryContact()
              }}
            >
              Yes, create
            </Button>
          </Stack>
        </Popover.Dropdown>
      </Popover>
      {error && <Text c="red">{error.message}</Text>}
    </>
  )
}
